import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';

import type { IdentityUserAri } from '@atlassian/ari/identity/user';
import type { PlatformSiteAri } from '@atlassian/ari/platform/site';
import { JSResourceForInteraction } from '@atlassian/react-async/js-resource-for-interaction';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, {
	type HomeRecentsListQuery,
} from './recents-list/__generated__/HomeRecentsListQuery.graphql';
import type { ExtraProps, RuntimeProps, ActivityType } from './recents-list/types';

export type EntryPointParams = {
	/**
	 * Picks the type of activity results to fetch.
	 * Note that workedOn will be forced if an actor is supplied
	 */
	activityType: ActivityType;
	/**
	 * Site ARI used to unit scope results. Will be required soon
	 */
	collabContextId?: PlatformSiteAri;
	/**
	 * Will FE filter out any results that don't match these ARIs. We're treating all site ARIs
	 * (platform, townsquare and others) as the same. We only look at the resourceId
	 */
	rootContainerIds?: PlatformSiteAri[];
	/**
	 * Number of results to fetch. Defaults to 50
	 */
	first?: number;
	/**
	 * Only supply if fetching results from a user that's NOT the current user.
	 * Will fetch a list of recently worked on items from the supplied user.
	 */
	actor?: IdentityUserAri;
	/**
	 * Used for contributor filtering
	 */
	currentUser: IdentityUserAri;
};

const fetchPolicy: PreloadFetchPolicy = 'store-and-network';

export const homeRecentsListEntryPoint: EntryPoint<
	EntryPointComponent<
		{
			queryReference: HomeRecentsListQuery;
		},
		Record<never, never>,
		RuntimeProps,
		ExtraProps
	>,
	EntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlassian/home-recents-entrypoint" */ './recents-list/HomeRecentsListQuery'
			).then((module) => ({ default: module.HomeRecentsListQuery })),
		{ moduleId: 'homeRecentsListEntryPoint' },
	),
	getPreloadProps: ({
		activityType,
		first,
		actor,
		collabContextId,
		rootContainerIds,
		currentUser,
	}: EntryPointParams) => ({
		queries: {
			queryReference: {
				parameters,
				variables: {
					/**
					 * These 3 variables are used to pick which activity data query to use
					 */
					isWorkedOn: !actor && activityType === 'workedOn',
					isViewed: !actor && activityType === 'viewed',
					isOtherUser: !!actor && activityType === 'workedOn',
					/**
					 * Used for unit scoping - will be mandatory soon. Waiting for the gate to roll out
					 */
					collabContextId: collabContextId?.toString(),
					/**
					 * These IDs are a mix of platform, townsquare and other site ARIs. If you don't provide
					 * every type of ARI (even if it has the same resourceId) items can unexpectedly be filtered
					 * Explicitly setting this to undefined for now and post filtering. Hoping the unit scoping will
					 * work in the future and we won't need these.
					 */
					rootContainerIds: undefined,
					first: first ?? 50,
					/**
					 * Used to get data for other users AND for viewed (current user)
					 * Viewed currently uses the `all` section of AP as eventTypes only work on that query
					 */
					actors: actor != null ? [actor.toString()] : [currentUser.toString()],
					/**
					 * Opting in to only querying `viewed` and `sl-clicked` for the viewed query.
					 * This is to avoid `sl-viewed` events that tend to drown out the feed.
					 */
					eventTypes: activityType === 'viewed' ? ['viewed', 'sl-clicked'] : undefined,
				},
				options: {
					fetchPolicy,
				},
			},
		},
		extraProps: {
			currentUser: currentUser,
			actorUser: actor,
			rootContainerIds: rootContainerIds,
		},
	}),
});
